import React, { useState } from "react";
import styled from "styled-components";
import { FaMoneyBill, FaRegCalendarAlt, FaShoppingCart } from "react-icons/fa";
import { MdAccountBalanceWallet } from "react-icons/md";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const Input = styled.input`
  padding: 10px;
  margin-bottom: 10px;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-sizing: border-box;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  margin-top: 10px;
`;

const DetailsContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
`;

const Detail = styled.div`
  margin-bottom: 10px;
  font-size: 16px;

  span {
    font-weight: bold;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  width: 100%;
  padding: 10px;
`;


const IconContainer = styled.div`
  margin-bottom: 15px;
  color: #007bff;
`;

const Card = styled.div`
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CardText = styled.div`
  h3 {
    font-size: 1.2rem;
    color: #333;
    margin-bottom: 10px;
  }

  p {
    font-size: 1rem;
    color: #666;
    font-weight: bold;
  }
`;

const OrdersContainer = styled.div`
  width: 100%;
  margin-top: 40px;
`;

const OrdersTitle = styled.h2`
  font-size: 1.5rem;
  color: rgba(0,0,255,0.5);
  margin-bottom: 20px;
`;


const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const OrderItem = styled.div`
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #fff;

  p {
    margin: 5px 0;
    font-size: 0.9rem;

    strong {
      font-weight: bold;
    }
  }
`;

const AffiliateSearchAndDetails = () => {
  const [referralCode, setReferralCode] = useState("");
  const [affiliate, setAffiliate] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  
  const calculateNextSettlementDate = () => {
    const today = new Date();
    const nextDate = new Date(today);
    nextDate.setMonth(today.getMonth() + 1); // Move to next month
    return nextDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };


  const [affiliateData, setAffiliateData] = useState({
    numberOfSales: 0,
    totalAffiliateAmount: 0,
    amountSettled: 0,
    amountToBeSettled: 0,
    nextSettlementDate: calculateNextSettlementDate(),
  });



  const handleSearch = async () => {
    if (!referralCode.trim()) {
      setError("Please enter a referral code.");
      return;
    }

    setError("");
    setLoading(true);
    setAffiliate(null);

    try {
      const response = await fetch(
        "https://elexdondigitalacademy.com/api/get_affiliate_by_referral_code.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ referralCode }),
        }
      );
      const data = await response.json();

      if (data.success) {
        setAffiliate(data.affiliate);
        fetchOrders();

      } else {
        setError(data.error);
      }
    } catch (err) {
      setError("An error occurred while fetching affiliate details.");
    } finally {
      setLoading(false);
    }
  };

  

  const fetchOrders = async () => {
    try {
      const response = await fetch("https://elexdondigitalacademy.com/api/fetch_orders_by_referrer_code.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referrer_code: referralCode }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch orders.");
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setOrders(data);
        setReferralCode("")
        calculateAffiliateData(data);
      } else {
        setError("No orders found.");
      }
    } catch (err) {
      setError(err.message);  
    } finally {
      setLoading(false);
    }
  };

  const calculateAffiliateData = (orders) => {
    const totalSales = orders.length;
    const totalAmount = orders.reduce((sum, order) => sum + (order.total_amount*20/100 || 0), 0);

    setAffiliateData((prevState) => ({
      ...prevState,
      numberOfSales: totalSales,
      totalAffiliateAmount: totalAmount,
      amountToBeSettled: totalAmount - prevState.amountSettled, // Adjust as needed
    }));
  };

  return (
    <Container>
      <h2 style={{color:"rgba(0,0,255,0.5)"}}>Affiliate Search</h2> 
      <Input
        type="text"
        placeholder="Enter Referral Code"
        value={referralCode}
        onChange={(e) => setReferralCode(e.target.value)}
      />
      <Button onClick={handleSearch}>Search</Button>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {loading && <p>Loading...</p>}
      {affiliate && (
        <DetailsContainer>
          <Detail>
            <span>Name:</span> {affiliate.first_name} {affiliate.last_name}
          </Detail>
          <Detail>
            <span>Email:</span> {affiliate.email}
          </Detail>
          <Detail>
            <span>Phone:</span> {affiliate.phone}
          </Detail>
          <Detail>
            <span>Bank Name:</span> {affiliate.bank_name}
          </Detail>
          <Detail>
            <span>Account Number:</span> {affiliate.bank_account_number}
          </Detail>
          <Detail>
            <span>Referral Code:</span> {affiliate.referral_code}
          </Detail>
        </DetailsContainer>
      )}
      
      {affiliate && <CardsContainer>
        <Card>
          <IconContainer>
            <FaShoppingCart size={30} />
          </IconContainer>
          <CardText>
            <h3>Number of Sales</h3>
            <p>{affiliateData.numberOfSales}</p>
          </CardText>
        </Card>
        <Card>
          <IconContainer>
            <FaMoneyBill size={30} />
          </IconContainer>
          <CardText>
            <h3>Total Affiliate Amount</h3>
            <p>₦{affiliateData.totalAffiliateAmount}</p>
          </CardText>
        </Card>
        <Card>  
          <IconContainer>
            <MdAccountBalanceWallet size={30} />
          </IconContainer>
          <CardText>
            <h3>Amount Settled</h3>
            <p>₦{affiliateData.amountSettled}</p>
          </CardText>
        </Card>
        <Card>
          <IconContainer>
            <MdAccountBalanceWallet size={30} />
          </IconContainer>
          <CardText>
            <h3>Amount to be Settled</h3>
            <p>₦{affiliateData.amountToBeSettled}</p>
          </CardText>
        </Card>
        <Card>
          <IconContainer>
            <FaRegCalendarAlt size={30} />
          </IconContainer>
          <CardText>
            <h3>Next Settlement Date</h3>
            <p>{affiliateData.nextSettlementDate}</p>
          </CardText>
        </Card>
      </CardsContainer>}


      {affiliate && <OrdersContainer>
        <OrdersTitle>Sales</OrdersTitle>
        {loading ? (
          <p>Loading sales...</p>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : orders.length > 0 ? (
          <OrdersList>
            {orders.map((order) => (
              <OrderItem key={order.id}>
                <p>
                  <strong>Date:</strong> {order.order_date}
                </p>
                <p>
                  <strong>Transaction Ref:</strong> {order.transaction_reference}
                </p>
                <p>
                  <strong>Name:</strong> {order.name}
                </p>
                <p>
                  <strong>Email:</strong> {order.email}
                </p>
                <p>
                  <strong>Total Amount:</strong> ₦{order.total_amount}
                </p>
              </OrderItem>
            ))}
          </OrdersList>
        ) : (
          <p>No sales found.</p>
        )}
      </OrdersContainer>}
    </Container>
  );
};

export default AffiliateSearchAndDetails;
